'use client';
import { FC, useEffect, useState } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPlayPause,
  CarouselPrevious,
} from 'components/ui/carousel';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'helpers/utils/classnames';
import { gtmHeroInteraction } from 'helpers/utils/gtm-events/gtmEccomerce';
import AmplienceHeroBanner, { AmplienceHeroBannerProps } from '../hero-banner';

export interface AmplienceBannerCarouselProps {
  carouselItems: AmplienceHeroBannerProps[];
  scrollInterval?: number;
}

const AmplienceBannerCarousel: FC<AmplienceBannerCarouselProps> = ({ carouselItems, scrollInterval = 5 }) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [clonedItems, setClonedItems] = useState<AmplienceHeroBannerProps[]>(carouselItems);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() % carouselItems.length);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() % carouselItems.length);
    });
  }, [api]);

  useEffect(() => {
    if (isMounted) {
      const totalWidth = window.innerWidth;
      const itemWidth = 1440;

      // Necessary items to clone
      const numItemsToClone = Math.ceil(totalWidth / itemWidth);

      // Duplicate clones
      let newClones: AmplienceHeroBannerProps[] = [];
      for (let i = 0; i < numItemsToClone; i++) {
        newClones = [...newClones, ...carouselItems];
      }
      setClonedItems(newClones);
    }
  }, [carouselItems, isMounted]);

  function onUserNavigate(index: number) {
    const item = carouselItems[index % carouselItems.length];
    gtmHeroInteraction('scroll', `${item.title} ${item.subtitle ? ` | ${item.subtitle}` : ''}`, index);
  }

  if (!isMounted) return <Skeleton className="mx-auto h-[630px] max-w-desktop rounded-xl bg-black/5" />;

  return (
    <Carousel
      plugins={[Autoplay({ delay: scrollInterval * 1000, stopOnInteraction: false })]}
      setApi={setApi}
      opts={{ loop: true, align: 'center' }}
      className="relative inset-x-1/2 -mx-[50vw] w-screen"
      onUserNavigate={onUserNavigate}
    >
      <CarouselContent className="flex">
        {clonedItems.map((carouselItem, index) => (
          <CarouselItem
            aria-hidden={index !== current}
            aria-label={`Carousel ${index + 1} of ${carouselItems.length}`}
            className="mx-1 flex min-h-[630px] w-full max-w-[1440px] p-0"
            key={`carosuel-banner-${carouselItem.title}-${index}`}
            id={`carousel-item-${index}`}
          >
            <AmplienceHeroBanner
              index={index}
              isFromCarousel
              isOnCarouselView={index % carouselItems.length === current}
              {...carouselItem}
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarosuelArrowsContainer current={current} carouselItemsLength={carouselItems.length} />
      <CarouselDotsContainer current={current} length={carouselItems.length} />
    </Carousel>
  );
};

const CarosuelArrowsContainer = ({
  current,
  carouselItemsLength,
}: {
  current: number;
  carouselItemsLength: number;
}) => {
  return (
    <>
      <CarouselPrevious
        aria-label="Previous"
        aria-controls={`carousel-item-${current - 1 < 0 ? carouselItemsLength - 1 : current - 1}`}
        className="carousel-previous flex h-[35px] w-[28px] items-center justify-center rounded-none rounded-r-lg border-none bg-white/40"
      />
      <CarouselNext
        aria-label="Next"
        aria-controls={`carousel-item-${current + 1 > carouselItemsLength - 1 ? 0 : current + 1}`}
        className="carousel-next flex h-[35px] w-[28px] items-center justify-center rounded-none rounded-l-lg border-none bg-white/40"
      />
    </>
  );
};

const CarouselDotsContainer = ({ length, current }: { length: number; current: number }) => {
  return (
    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/40 px-[10px] py-1.5">
      {Array.from({ length: length }).map((_, idx) => (
        <div
          data-testid={`carousel-dot-${idx}`}
          key={`carousel-dot-${idx}`}
          className={cn('size-[18px] rounded-full transition-all', {
            'bg-[#222222]': idx % length === current,
            'bg-[#edededed]': idx % length !== current,
          })}
        />
      ))}
      <CarouselPlayPause className="ml-2" />
    </div>
  );
};

export default AmplienceBannerCarousel;
